import { useMemo } from 'react';
import { usePastAnnualRevenueEarnings } from '../usePastAnnualRevenueEarnings/usePastAnnualRevenueEarnings';
import {
  CreateNarrativeFuturePEMultipleCalculator,
  FuturePEMultipleContext,
  hasOnlyFairValue,
  useFuturePEMultipleCalculation,
  ViewEditNarrativeFuturePEMultipleCalculator,
  calculateValuationInitialisation,
} from '../../components/Calculators';
import { isLatestPublishedUpdateLocked } from '../../components/PublicNarrativeEdit/utils';
import type { Narrative } from '../useNarrative';
import type {
  NarrativeCompanyLatestPublishedUpdate,
  NarrativeCompanyDraftUpdate,
  MyNarrativeCompany,
} from '../useNarrativeCompany';

type Props = {
  narrative?: Narrative;
  isUpdateNarrativeUpdateEnabled: boolean;
  latestPublishedUpdate?: NarrativeCompanyLatestPublishedUpdate;
  draftUpdate?: NarrativeCompanyDraftUpdate;
  company?: MyNarrativeCompany['Company'] | Narrative['company'];
};

export const useNarrativeValuationManager = ({
  narrative,
  isUpdateNarrativeUpdateEnabled,
  latestPublishedUpdate,
  draftUpdate,
  company: companyFromValuationCompanyResponse,
}: Props) => {
  const { company: companyFromNarrative, valuation: deprecatedValuation } =
    narrative || {};

  const company = companyFromNarrative || companyFromValuationCompanyResponse;

  if (!company) {
    throw new Error("Narrative's company is missing");
  }

  const valuation = useMemo(() => {
    if (
      isUpdateNarrativeUpdateEnabled &&
      latestPublishedUpdate &&
      !isLatestPublishedUpdateLocked(latestPublishedUpdate)
    ) {
      return latestPublishedUpdate.valuation;
    }

    if (
      isUpdateNarrativeUpdateEnabled &&
      draftUpdate &&
      draftUpdate.valuation
    ) {
      return draftUpdate.valuation;
    }

    return deprecatedValuation ?? null;
  }, [
    isUpdateNarrativeUpdateEnabled,
    latestPublishedUpdate,
    deprecatedValuation,
    draftUpdate,
  ]);

  const pastAnnualRevenueEarnings = usePastAnnualRevenueEarnings({
    company,
    fiscalData: valuation?.fiscalData ?? null,
  });

  const strategyContext = useMemo(
    () =>
      new FuturePEMultipleContext(
        !valuation || hasOnlyFairValue(valuation)
          ? new CreateNarrativeFuturePEMultipleCalculator(company)
          : new ViewEditNarrativeFuturePEMultipleCalculator(valuation)
      ),
    [company, valuation]
  );

  const {
    calculateFairValue,
    calculateFairValueFromInitialisation,
    calculateRevenueGrowthRateFromFairValue,
    calculateBreakDown,
  } = useFuturePEMultipleCalculation(strategyContext);

  return {
    valuation,
    pastAnnualRevenueEarnings,
    calculateFairValue,
    calculateFairValueFromInitialisation,
    calculateRevenueGrowthRateFromFairValue,
    calculateBreakDown,
    calculateValuationInitialisation,
  };
};
